// frontend/src/pages/freelancer/FreelancerSidebarLayout.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaHome,
  FaUser,
  FaCalendarAlt,
  FaList,
  FaCog,
  FaDollarSign,
  FaBox,
  FaBell,
  FaChartLine,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaEllipsisH,
  FaClipboardList,
  FaImages,
  FaHandshake,
} from "react-icons/fa";
import { APP_LOGO_URL } from "../../config/branding";
import NotificationBell from "../../components/NotificationBell";
import LogoutConfirmModal from "../../components/LogoutConfirmModal";
import "./freelancerStyles.css";

function FreelancerSidebarLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isBoothRenter, setIsBoothRenter] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const SIDEBAR_COLLAPSE_KEY = "ogestyle_sidebar_collapsed_freelancer";

  // Base path changes to booth-renter for R7 users
  const basePath = isBoothRenter
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";
  const dashboardPath = basePath;

  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    setIsLoggedIn(!!token);

    // Check if user is R7 (booth renter)
    const rolesStr = localStorage.getItem("ogestyle_roles");
    if (rolesStr) {
      try {
        const roles = JSON.parse(rolesStr);
        setIsBoothRenter(roles.includes("R7"));
      } catch (e) {}
    }

    const savedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSE_KEY);
    if (savedCollapsed === "1") {
      setCollapsed(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const handleLogout = () => {
    localStorage.removeItem("ogestyle_token");
    localStorage.removeItem("ogestyle_user");
    setIsLoggedIn(false);
    navigate("/login");
  };

  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <div className="dashboard-layout theme-typography">
      {/* Desktop Sidebar */}
      <aside
        className={`dashboard-sidebar ${collapsed ? "collapsed" : ""}${mobileMenuOpen ? " mobile-open" : ""}`}
      >
        <div className="sidebar-header">
          {!collapsed && (
            <img
              src={APP_LOGO_URL}
              alt="OGESTYLE Logo"
              className="sidebar-logo-image"
            />
          )}
          {!collapsed && (
            <NotificationBell viewAllPath="/dashboard/freelancer/notifications" />
          )}
          <button
            className="sidebar-toggle"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <FaBars />
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul
            className="menu-list"
            onClick={(event) => {
              if (window.innerWidth <= 900 && event.target.closest("a")) {
                setMobileMenuOpen(false);
              }
            }}
          >
            <li
              className={`menu-item ${location.pathname === dashboardPath || (isBoothRenter && location.pathname === "/dashboard/booth-renter") ? "active" : ""}`}
            >
              <Link to={dashboardPath}>
                <span className="menu-icon">
                  <FaHome />
                </span>
                <span className="menu-text">Dashboard</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/profile`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/profile`}>
                <span className="menu-icon">
                  <FaUser />
                </span>
                <span className="menu-text">Profile</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/services`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/services`}>
                <span className="menu-icon">
                  <FaList />
                </span>
                <span className="menu-text">Services</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/schedule`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/schedule`}>
                <span className="menu-icon">
                  <FaCalendarAlt />
                </span>
                <span className="menu-text">Schedule</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/bookings`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/bookings`}>
                <span className="menu-icon">
                  <FaCalendarAlt />
                </span>
                <span className="menu-text">Bookings</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/looks`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/looks`}>
                <span className="menu-icon">
                  <FaImages />
                </span>
                <span className="menu-text">My Looks</span>
              </Link>
            </li>
            {/* I1: "Service Inventory" was hidden because the page is a stub. It is
                VISIBLE again (2026-09-27) so the route can be inspected directly
                instead of the menu entry being an unexplained gap. The page now
                states plainly that nothing is implemented behind this route rather
                than silently bouncing to Products, which is what made the earlier
                archaeology inconclusive. */}
            <li
              className={`menu-item ${isActive(`${basePath}/inventory`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/inventory`}>
                <span className="menu-icon">🧴</span>
                <span className="menu-text">Service Inventory</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/customers`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/customers`}>
                <span className="menu-icon">👥</span>
                <span className="menu-text">Customers</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/products`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/products`}>
                <span className="menu-icon">
                  <FaBox />
                </span>
                <span className="menu-text">Products</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/orders`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/orders`}>
                <span className="menu-icon">🛍️</span>
                <span className="menu-text">Orders</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/payouts`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/payouts`}>
                <span className="menu-icon">
                  <FaDollarSign />
                </span>
                <span className="menu-text">Payouts</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/analytics`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/analytics`}>
                <span className="menu-icon">💰</span>
                <span className="menu-text">Performance Analytics</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/marketing`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/marketing`}>
                <span className="menu-icon">📨</span>
                <span className="menu-text">Marketing</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive("/dashboard/referrals") ? "active" : ""}`}
            >
              <Link to="/dashboard/referrals">
                <span className="menu-icon">
                  <FaHandshake />
                </span>
                <span className="menu-text">Refer &amp; earn</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/reviews`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/reviews`}>
                <span className="menu-icon">⭐</span>
                <span className="menu-text">Reviews</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/waitlist`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/waitlist`}>
                <span className="menu-icon">
                  <FaClipboardList />
                </span>
                <span className="menu-text">Waitlist</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/notifications`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/notifications`}>
                <span className="menu-icon">
                  <FaBell />
                </span>
                <span className="menu-text">Notifications</span>
              </Link>
            </li>
            <li
              className={`menu-item ${isActive(`${basePath}/settings`) ? "active" : ""}`}
            >
              <Link to={`${basePath}/settings`}>
                <span className="menu-icon">
                  <FaCog />
                </span>
                <span className="menu-text">Settings</span>
              </Link>
            </li>
          </ul>
        </nav>

        <div className="sidebar-footer">
          <button
            onClick={() => setShowLogoutModal(true)}
            className="logout-button"
            aria-label="Logout"
          >
            <FaSignOutAlt />
            <span className="menu-text">Logout</span>
          </button>
        </div>
      </aside>

      {mobileMenuOpen && (
        <div
          className="mobile-sidebar-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="mobile-sidebar-topbar">
        <button
          className="mobile-sidebar-toggle"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Open menu"
        >
          <FaBars />
        </button>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav">
        <Link
          to={basePath}
          className={
            isActive(basePath) && location.pathname === basePath ? "active" : ""
          }
        >
          <FaHome className="nav-icon" />
          <span>Home</span>
        </Link>
        <Link
          to={`${basePath}/services`}
          className={isActive(`${basePath}/services`) ? "active" : ""}
        >
          <FaList className="nav-icon" />
          <span>Services</span>
        </Link>
        <Link
          to={`${basePath}/bookings`}
          className={isActive(`${basePath}/bookings`) ? "active" : ""}
        >
          <FaCalendarAlt className="nav-icon" />
          <span>Bookings</span>
        </Link>
        <Link
          to={`${basePath}/looks`}
          className={isActive(`${basePath}/looks`) ? "active" : ""}
        >
          <FaImages className="nav-icon" />
          <span>Looks</span>
        </Link>
        <Link
          to={`${basePath}/payouts`}
          className={isActive(`${basePath}/payouts`) ? "active" : ""}
        >
          <FaDollarSign className="nav-icon" />
          <span>Payouts</span>
        </Link>
        <Link
          to="/dashboard/referrals"
          className={isActive("/dashboard/referrals") ? "active" : ""}
        >
          <FaHandshake className="nav-icon" />
          <span>Referrals</span>
        </Link>
        <button
          onClick={() => setShowMoreMenu(!showMoreMenu)}
          className={showMoreMenu ? "active" : ""}
        >
          <FaEllipsisH className="nav-icon" />
          <span>More</span>
        </button>
      </nav>

      {/* Mobile More Menu */}
      {showMoreMenu && (
        <div className="mobile-more-menu">
          <div
            className="more-menu-backdrop"
            onClick={() => setShowMoreMenu(false)}
          />
          <div className="more-menu-content">
            <Link
              to={`${basePath}/profile`}
              onClick={() => setShowMoreMenu(false)}
            >
              <FaUser /> Profile
            </Link>
            <Link
              to={`${basePath}/schedule`}
              onClick={() => setShowMoreMenu(false)}
            >
              <FaCalendarAlt /> Schedule
            </Link>
            <Link
              to={`${basePath}/looks`}
              onClick={() => setShowMoreMenu(false)}
            >
              <FaImages /> My Looks
            </Link>
            <Link
              to={`${basePath}/customers`}
              onClick={() => setShowMoreMenu(false)}
            >
              👥 Customers
            </Link>
            <Link
              to={`${basePath}/orders`}
              onClick={() => setShowMoreMenu(false)}
            >
              🛍️ Orders
            </Link>
            <Link
              to={`${basePath}/payouts`}
              onClick={() => setShowMoreMenu(false)}
            >
              <FaDollarSign /> Payouts
            </Link>
            <Link
              to={`${basePath}/analytics`}
              onClick={() => setShowMoreMenu(false)}
            >
              💰 Performance Analytics
            </Link>
            <Link
              to={`${basePath}/marketing`}
              onClick={() => setShowMoreMenu(false)}
            >
              📨 Marketing
            </Link>
            <Link
              to="/dashboard/referrals"
              onClick={() => setShowMoreMenu(false)}
            >
              <FaHandshake /> Refer &amp; earn
            </Link>
            <Link
              to={`${basePath}/reviews`}
              onClick={() => setShowMoreMenu(false)}
            >
              ⭐ Reviews
            </Link>
            <Link
              to={`${basePath}/waitlist`}
              onClick={() => setShowMoreMenu(false)}
            >
              <FaClipboardList /> Waitlist
            </Link>
            <Link
              to={`${basePath}/notifications`}
              onClick={() => setShowMoreMenu(false)}
            >
              <FaBell /> Notifications
            </Link>
            <Link
              to={`${basePath}/settings`}
              onClick={() => setShowMoreMenu(false)}
            >
              <FaCog /> Settings
            </Link>
            <button
              onClick={() => {
                setShowMoreMenu(false);
                setShowLogoutModal(true);
              }}
              className="logout-link"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="dashboard-main">{children}</main>

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}

export default FreelancerSidebarLayout;
